import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import GoogleAsset from '../../assets/icons8-google 1.png';
import AzureAsset from '../../assets/Frame 59.png';
import { useNavigate } from 'react-router-dom';
import Logo from '../../assets/Logo.png';
import AuthImage from '../../assets/auth.img.png';
import { loginWithCredentials, ApiError, LoginResult } from '../../utils/auth';

interface LoginPageProps {
  onLogin: (user: UserData) => void;
}

interface UserData {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'Manager' | 'Operator' | 'Auditor';
  avatar?: string;
  permissions: string[];
  sites: string[];
}

// Local admin account — remove once backend admin login endpoint is live
const LOCAL_ADMIN = {
  email:     'admin@petrotag360.com',
  passwords: ['admin 123', 'admin123'],
  user: {
    id:          'local-admin-1',
    name:        'PetroTag Admin',
    email:       'admin@petrotag360.com',
    role:        'Admin' as const,
    permissions: ['all'] as string[],
    sites:       ['all'] as string[],
  },
};

const VALID_ROLES = ['Admin', 'Manager', 'Operator', 'Auditor'] as const;
type Role = typeof VALID_ROLES[number];

function toRole(raw: unknown): Role {
  if (typeof raw === 'string' && VALID_ROLES.includes(raw as Role)) return raw as Role;
  return 'Operator';
}

function nameFromEmail(email: string): string {
  return email
    .split('@')[0]
    .replace(/[._-]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
    setError('');
  };

  const handleLogin = async () => {
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      return;
    }
    setLoading(true);
    setError('');

    // Local admin bypass — remove once backend admin login endpoint is live
    if (
      formData.email.trim().toLowerCase() === LOCAL_ADMIN.email &&
      LOCAL_ADMIN.passwords.includes(formData.password.trim())
    ) {
      onLogin(LOCAL_ADMIN.user);
      setLoading(false);
      return;
    }

    try {
      const { user }: LoginResult = await loginWithCredentials(formData.email, formData.password);

      onLogin({
        id:          user.id,
        name:        user.username || user.company_name || nameFromEmail(user.email),
        email:       user.email,
        role:        'Operator',
        permissions: ['view_sites', 'monitor_fuel', 'view_alerts'],
        sites:       ['all'],
      });
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.status != null) {
        setError(apiErr.message);
      } else {
        setError('Unable to reach the server. Please check your connection.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleLogin();
  };

  const handleSocialSignIn = (provider: 'google' | 'microsoft') => {
    setLoading(true);
    setTimeout(() => {
      onLogin({
        id: Math.random().toString(36).slice(2),
        name: `${provider === 'google' ? 'Google' : 'Microsoft'} User`,
        email: `user@${provider}.com`,
        role: 'Operator',
        permissions: ['view_sites', 'monitor_fuel', 'view_alerts'],
        sites: ['all'],
      });
      setLoading(false);
    }, 1500);
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#FFFFFF',
      fontSize: '0.875rem',
      height: '43px',
      gap: '24px',
      opacity: 1,
      '& fieldset': { borderColor: 'rgba(217, 217, 217, 1)', borderWidth: '1px' },
      '&:hover fieldset': { borderColor: 'rgba(217, 217, 217, 1)', borderWidth: '1px' },
      '&.Mui-focused fieldset': { borderColor: 'rgba(217, 217, 217, 1) !important', borderWidth: '1px !important' },
      '& input': { padding: '10px 10px' },
      '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
        WebkitBoxShadow: '0 0 0 100px #ffffff inset',
        WebkitTextFillColor: '#000000',
        caretColor: '#000000',
      },
    },
  };

  const labelSx = {
    fontFamily: '"Montserrat", sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '100%',
    letterSpacing: '0%',
    color: 'rgba(0, 0, 0, 1)',
    mb: 0.5,
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

      {/* ── LEFT PANEL ─────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: '#ffffff',
          height: '100%',
          overflowY: 'auto',
          pt: { xs: '32px', md: '48px' },
          pb: '32px',
        }}
      >
      <Box
        sx={{
          width: { xs: '100%', md: '404px' },
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 3, sm: 4, md: 0 },
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 3 }}>
          <Box
            component="img"
            src={Logo}
            alt="PetroTag360"
            sx={{ height: { xs: 56, md: 80 }, objectFit: 'contain', objectPosition: 'left' }}
          />
        </Box>

        {/* Heading */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontFamily: '"Unbounded", sans-serif',
              fontWeight: 700,
              fontSize: { xs: '26px', md: '33.31px' },
              lineHeight: { xs: '36px', md: '48px' },
              letterSpacing: 0,
              color: 'rgba(0, 0, 0, 1)',
              opacity: 0.8,
            }}
          >
            Welcome back!
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: 0,
              color: 'rgba(87, 81, 81, 1)',
            }}
          >
            Fuel tracking made transparent, smart, and reliable
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        {/* Form */}
        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>

          {/* Email */}
          <Typography sx={labelSx}>Email address</Typography>
          <TextField
            fullWidth
            placeholder="Enter your email"
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            onKeyPress={handleKeyPress}
            disabled={loading}
            size="small"
            sx={{ ...inputSx, mb: 1.5 }}
          />

          {/* Password */}
          <Typography sx={labelSx}>Password</Typography>
          <TextField
            fullWidth
            placeholder="Enter your password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange('password')}
            onKeyPress={handleKeyPress}
            disabled={loading}
            size="small"
            sx={{ ...inputSx, mb: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Remember me + Forgot password */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  sx={{ color: '#D1D5DB', '&.Mui-checked': { color: '#1D4ED8' }, p: '6px' }}
                />
              }
              label={
                <Typography sx={{ fontSize: '0.82rem', color: '#374151' }}>
                  Remember for 30 days
                </Typography>
              }
              sx={{ m: 0 }}
            />
            <Link
              component="button"
              type="button"
              onClick={() => navigate('/forgot-password')}
              underline="hover"
              sx={{
                fontSize: '0.82rem',
                fontWeight: 500,
                color: '#F59E0B',
                '&:hover': { color: '#D97706' },
              }}
            >
              Forgot password
            </Link>
          </Box>

          {/* Login button */}
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              height: '43px',
              backgroundColor: 'rgba(43, 103, 176, 1)',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: 700,
              textTransform: 'none',
              letterSpacing: 0.3,
              boxShadow: 'none',
              mb: 0,
              '&:hover': { backgroundColor: 'rgba(33, 85, 150, 1)', boxShadow: 'none' },
              '&:disabled': { backgroundColor: '#93C5FD' },
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} sx={{ color: '#fff' }} />
                Signing in...
              </Box>
            ) : 'Login'}
          </Button>

          {/* Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2 }}>
            <Divider sx={{ flex: 1, borderColor: '#E5E7EB' }} />
            <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF', whiteSpace: 'nowrap' }}>Or</Typography>
            <Divider sx={{ flex: 1, borderColor: '#E5E7EB' }} />
          </Box>

          {/* Social buttons */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={() => handleSocialSignIn('google')}
              disabled={loading}
              startIcon={<Box component="img" src={GoogleAsset} alt="Google" sx={{ width: 18, height: 18 }} />}
              sx={{
                height: '43px',
                borderColor: '#E5E7EB',
                color: '#374151',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.82rem',
                backgroundColor: '#fff',
                boxShadow: 'none',
                '&:hover': { borderColor: '#D1D5DB', backgroundColor: '#F9FAFB', boxShadow: 'none' },
              }}
            >
              Sign in with Google
            </Button>

            <Button
              variant="outlined"
              onClick={() => handleSocialSignIn('microsoft')}
              disabled={loading}
              startIcon={<Box component="img" src={AzureAsset} alt="Microsoft" sx={{ width: 18, height: 18 }} />}
              sx={{
                height: '43px',
                borderColor: '#E5E7EB',
                color: '#374151',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.82rem',
                backgroundColor: '#fff',
                boxShadow: 'none',
                '&:hover': { borderColor: '#D1D5DB', backgroundColor: '#F9FAFB', boxShadow: 'none' },
              }}
            >
              Sign in with Microsoft
            </Button>
          </Box>

          {/* Sign up link */}
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                color: '#6B7280',
              }}
            >
              Don't have an account?{' '}
              <Link
                component="button"
                type="button"
                onClick={() => navigate('/signup')}
                underline="hover"
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  color: 'rgba(43, 103, 176, 1)',
                  verticalAlign: 'baseline',
                }}
              >
                Create Account
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
      </Box>

      {/* ── RIGHT PANEL ────────────────────────────────────────────── */}
      <Box
        component="img"
        src={AuthImage}
        alt=""
        sx={{
          display: { xs: 'none', md: 'block' },
          width: 'calc(100vh * 1576 / 2048)',
          height: '100vh',
          flexShrink: 0,
          objectFit: 'fill',
        }}
      />

    </Box>
  );
};

export default LoginPage;
